import React, { useEffect, useState } from "react";
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaTimesCircle, FaTimes } from "react-icons/fa";

export default function Toast({ toast, onRemove }) {
  const [progress, setProgress] = useState(100);
  const [isExiting, setIsExiting] = useState(false);

  useEffect(() => {
    if (toast.autoClose !== false) {
      const duration = toast.duration || 3000;
      const intervalTime = 50; // Update every 50ms for smooth progress
      const decrement = (100 / duration) * intervalTime;

      const progressInterval = setInterval(() => {
        setProgress((prev) => {
          const newProgress = prev - decrement;
          if (newProgress <= 0) {
            clearInterval(progressInterval);
            return 0;
          }
          return newProgress;
        });
      }, intervalTime);

      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => {
          onRemove(toast.id);
        }, 300); // Match animation duration
      }, duration);

      return () => {
        clearTimeout(timer);
        clearInterval(progressInterval);
      };
    }
  }, [toast, onRemove]);

  const handleClose = () => {
    setIsExiting(true);
    setTimeout(() => {
      onRemove(toast.id);
    }, 300);
  };

  const getToastStyles = () => {
    const baseStyles = "relative flex items-center gap-3 px-4 py-3 rounded-lg shadow-xl w-full overflow-hidden";
    const animationClass = isExiting ? "" : "animate-slide-in";
    
    switch (toast.type) {
      case "success":
        return `${baseStyles} ${animationClass} bg-white border-l-4 border-[#f26522] text-gray-800 shadow-[#f26522]/10`;
      case "error":
        return `${baseStyles} ${animationClass} bg-white border-l-4 border-red-500 text-gray-800 shadow-red-100/50`;
      case "warning":
        return `${baseStyles} ${animationClass} bg-white border-l-4 border-[#FF8C42] text-gray-800 shadow-[#FF8C42]/10`;
      case "info":
      default:
        return `${baseStyles} ${animationClass} bg-white border-l-4 border-[#f26522] text-gray-800 shadow-[#f26522]/10`;
    }
  };

  const getIcon = () => {
    const iconClass = "w-5 h-5 flex-shrink-0";
    switch (toast.type) {
      case "success":
        return <FaCheckCircle className={`${iconClass} text-[#f26522]`} />;
      case "error":
        return <FaTimesCircle className={`${iconClass} text-red-500`} />;
      case "warning":
        return <FaExclamationTriangle className={`${iconClass} text-[#FF8C42]`} />;
      case "info":
      default:
        return <FaInfoCircle className={`${iconClass} text-[#f26522]`} />;
    }
  };

  const getProgressColor = () => {
    switch (toast.type) {
      case "success":
        return "bg-[#f26522]";
      case "error":
        return "bg-red-500";
      case "warning":
        return "bg-[#FF8C42]";
      case "info":
      default:
        return "bg-[#f26522]";
    }
  };

  return (
    <div 
      className={getToastStyles()}
      style={isExiting ? { animation: 'slide-out-right 0.3s ease-in forwards' } : {}}
    >
      {/* Progress bar */}
      {toast.autoClose !== false && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gray-100">
          <div
            className={`h-full ${getProgressColor()} transition-all ease-linear`}
            style={{ width: `${progress}%` }}
          />
        </div>
      )}
      
      {getIcon()}
      <div className="flex-1 min-w-0">
        {toast.title && (
          <div className="font-semibold text-sm mb-0.5 text-gray-900">{toast.title}</div>
        )}
        <div className="text-sm text-gray-700 leading-relaxed">{toast.message}</div>
      </div>
      <button
        onClick={handleClose}
        className="ml-2 text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0 p-1 rounded hover:bg-gray-100"
        aria-label="Close"
      >
        <FaTimes className="w-4 h-4" />
      </button>
    </div>
  );
}

